import React from 'react';
import {Box, Typography, Link as MuiLink} from '@mui/material';
import {CorporateFareOutlined, Close, ChevronRight} from '@mui/icons-material';
import {useListContext, usePermissions, useTranslate} from 'react-admin';
import {t} from '../../themes/statamicTokens';
import {useScope} from '../../scope/ScopeContext';
import {useUmbrellaChoices} from '../../lib/customHooks';
import useUmbrellasAndCharities from '../../filter/useUmbrellasAndCharities';

/**
 * Narrow strip shown at the top of a scoped list when umbrella_id or
 * charity_id/charity_external_id is active in filterValues. Clicking Clear
 * removes those params from the URL (session-only override); localStorage
 * scope is untouched, so re-navigating to the list will re-seed the filter.
 *
 * Shared across v1 lists (numeric umbrella_id/charity_id) and the v2 children
 * list (UUID umbrella_id/charity_external_id) — every id read from
 * filterValues is matched against choices in BOTH id spaces (numeric id or
 * external_id), since we don't know up front which space produced it.
 */
const ScopeBanner = () => {
    const {filterValues, setFilters, displayedFilters} = useListContext();
    const {umbrellaId: scopeUmbrellaId} = useScope();
    const {permissions} = usePermissions();
    const translate = useTranslate();
    const isAdmin = permissions === 'admin';

    // The umbrella dimension only makes sense for admins. For umbrella admins the
    // umbrella is implicit (they only ever scope down to a charity), so never
    // surface an umbrella crumb for them.
    const activeUmbrella = isAdmin ? (filterValues?.umbrella_id ?? null) : null;
    // v2 children uses charity_external_id; v1 lists use charity_id. Read whichever is set.
    const activeCharity = filterValues?.charity_external_id ?? filterValues?.charity_id ?? null;

    const matchesId = (choice, value) => String(choice.id) === String(value) || (choice.external_id != null && choice.external_id === value);

    const umbrellas = useUmbrellaChoices();
    const matchedUmbrella = activeUmbrella != null
        ? umbrellas.find(u => matchesId(u, activeUmbrella))
        : null;

    // useUmbrellasAndCharities scopes its charity fetch by a NUMERIC umbrella id. Pass the
    // resolved numeric id from the matched umbrella choice when we have one; otherwise skip
    // that narrowing (0 → unscoped charity list) rather than forwarding a possibly-UUID value.
    const resolvedUmbrellaId = matchedUmbrella?.id ?? scopeUmbrellaId ?? 0;
    const {charities} = useUmbrellasAndCharities({
        umbrellaId: resolvedUmbrellaId,
    });

    if (activeUmbrella == null && activeCharity == null) return null;

    const matchedCharity = activeCharity != null
        ? (charities ?? []).find(c => matchesId(c, activeCharity))
        : null;

    const umbrellaName = matchedUmbrella?.name;
    const charityName = matchedCharity?.charity_name ?? matchedCharity?.name;

    const handleClear = () => {
        const next = {...filterValues};
        delete next.umbrella_id;
        delete next.charity_id;
        delete next.charity_external_id;
        setFilters(next, displayedFilters);
    };

    // Never render a raw id (and definitely never a raw UUID) when a crumb value doesn't
    // resolve to a known choice — fall back to a generic label instead.
    const crumbs = [];
    if (activeUmbrella != null) {
        crumbs.push(umbrellaName || translate('Scoped'));
    }
    if (activeCharity != null) {
        crumbs.push(charityName || translate('Scoped'));
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 0.75,
                backgroundColor: t.color.accent.primaryTint,
                borderBottom: `1px solid ${t.color.border.default}`,
            }}
        >
            <CorporateFareOutlined sx={{fontSize: 14, color: t.color.accent.primary}}/>
            <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.accent.primary, fontWeight: 500}}>
                {translate('Scoped to')}
            </Typography>
            {crumbs.map((label, i) => (
                <Box key={i} sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}>
                    {i > 0 && (
                        <ChevronRight sx={{fontSize: 14, color: t.color.accent.primary, opacity: 0.6}}/>
                    )}
                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.primary, fontWeight: 500}}>
                        {label}
                    </Typography>
                </Box>
            ))}
            <Box sx={{flex: 1}}/>
            <MuiLink
                component="button"
                onClick={handleClear}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    fontSize: `${t.type.caption.size}px`,
                    color: t.color.accent.primary,
                    fontWeight: 500,
                    textDecoration: 'none',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    '&:hover': {textDecoration: 'underline'},
                }}
            >
                <Close sx={{fontSize: 14}}/>
                {translate('Clear on this list')}
            </MuiLink>
        </Box>
    );
};

export default ScopeBanner;
